import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
	MonthlyScoresParams,
	RankingParams,
	RankingPersonEventsParams,
} from '@/types'
import {
	getExcludedClusters,
	getMonthlyScores,
	getRanking,
	getRankingPersonEvents,
	getRoleWeights,
	updateExcludedClusters,
	updateMonthlyScores,
	updateRoleWeights,
} from '../services/scoring'

// ── Query keys ────────────────────────────────────────────────────────────────

export const scoringKeys = {
	all: ['scoring'] as const,
	monthlyScores: (params: MonthlyScoresParams) =>
		[...scoringKeys.all, 'monthly-scores', params] as const,
	roleWeights: () => [...scoringKeys.all, 'role-weights'] as const,
	ranking: (params: RankingParams) =>
		[...scoringKeys.all, 'ranking', params] as const,
	rankingPersonEvents: (personId: string, params: RankingPersonEventsParams) =>
		[...scoringKeys.all, 'ranking', 'person-events', personId, params] as const,
	excludedClusters: () => [...scoringKeys.all, 'excluded-clusters'] as const,
}

// ── Monthly scores ────────────────────────────────────────────────────────────

export function useMonthlyScores(params: MonthlyScoresParams) {
	return useQuery({
		queryKey: scoringKeys.monthlyScores(params),
		queryFn: () => getMonthlyScores(params),
	})
}

export function useUpdateMonthlyScores() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: updateMonthlyScores,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: scoringKeys.all })
			// catálogo e questionários exibem a nota vigente do mês corrente
			queryClient.invalidateQueries({ queryKey: ['catalog'] })
			queryClient.invalidateQueries({ queryKey: ['quiz'] })
		},
	})
}

// ── Role weights ──────────────────────────────────────────────────────────────

export function useRoleWeights() {
	return useQuery({
		queryKey: scoringKeys.roleWeights(),
		queryFn: getRoleWeights,
		select: (data) => data.items,
	})
}

export function useUpdateRoleWeights() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: updateRoleWeights,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: scoringKeys.all })
		},
	})
}

// ── Ranking ───────────────────────────────────────────────────────────────────

export function useRanking(params: RankingParams, enabled = true) {
	return useQuery({
		queryKey: scoringKeys.ranking(params),
		queryFn: () => getRanking(params),
		enabled,
	})
}

export function useRankingPersonEvents(
	personId: string | undefined,
	params: RankingPersonEventsParams,
) {
	return useQuery({
		queryKey: scoringKeys.rankingPersonEvents(personId ?? '', params),
		queryFn: () => getRankingPersonEvents(personId as string, params),
		enabled: !!personId,
	})
}

export function useExcludedClusters() {
	return useQuery({
		queryKey: scoringKeys.excludedClusters(),
		queryFn: getExcludedClusters,
		select: (data) => data.clusterIds,
	})
}

export function useUpdateExcludedClusters() {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: updateExcludedClusters,
		onSuccess: () => {
			// altera quem entra no ranking Geral
			queryClient.invalidateQueries({ queryKey: scoringKeys.all })
		},
	})
}
